import {
  ButtonBar,
  Modal,
  PrimaryButton,
  SecondaryButton,
  Text,
} from '@weave/design-system';
import { Template } from '@weave/schema-gen-ts/dist/schemas/phone/tenantprovisioner/tenantprovisioner_service.pb';
import { css, cx } from 'emotion';
import { Field, Form, FormikProps } from 'formik';
import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { CopyToClipboardButton } from '../../../components/shared/copy-to-clipboard/copy-to-clipboard-button';
import { isWeaveEmail } from '../../../utils/email-domain';
import { OTHER, injectParams } from '../../../constants/paths';
import { Timezones } from '../../../models/timezones';
import { getVerticalList } from '../../../models/vertical-enum';
import {
  LocationTypes,
  ProvisionLocationResponseDTO,
} from '../../../redux/actions/location';
import { history } from '../../../redux/store/history';
import { InfoTooltip } from '../../shared/info-tooltip/info-tooltip.component';
import { Loader } from '../../shared/loader/loader.component';
import { StatesTypeahead } from '../../shared/state-select/state-select.component';
import { ToggleSwitch } from '../../shared/toggle-switch/toggle-switch.component';
import { LocationSearchContainer } from '../location-search-container/location-search.container';
import { LocationAlert } from './location-alert.component';
import './location-form.component.css';
import {
  creationStatusButton,
  creationStatusContainer,
  creationStatusListItem,
  flex,
  formAction,
  formGroup,
  formLayout,
  inlineFormSpacer,
  loader,
  marginRight5,
  validationError,
  warningText,
} from './location-form.styles';

export interface LocationFormProps {
  creationStatus: ProvisionLocationResponseDTO;
  isNew: boolean;
  loading: boolean;
  allowSetParent: boolean;
  onClose: () => void;
  resetLocationCreationStatus: () => void;
}

export const LocationForm = (props: LocationFormProps & FormikProps<any>) => {
  const { state: routerState } = useLocation<{ state: { Type?: LocationTypes } }>();
  const stateValues = routerState?.state;

  const statesTypeaheadOnChange = (selected) => {
    if (selected.length > 0 && selected[0]) {
      props.setFieldValue('state', selected[0].id);
      props.setFieldValue('country', selected[0].country);
    }
  };

  useEffect(() => {
    stateValues?.Type && props.setFieldValue('Type', stateValues.Type);
  }, [stateValues?.Type]);

  const isExternalTruelarkLocation = props.values.Type === LocationTypes.External;
  const isWeaveUserWithExternalType =
    props.isNew && isExternalTruelarkLocation && isWeaveEmail(props.values.Email);

  const closeCreationStatusModal = () => {
    const locationId = props.creationStatus.locationId;
    if (locationId) {
      history.push(injectParams(OTHER.dashboard, { id: locationId }));
    } else {
      history.push('/');
    }
    props.resetLocationCreationStatus();
  };

  const renderButtons = () => {
    return (
      <div
        className={css`
          text-align: center;
        `}
      >
        <ButtonBar>
          <SecondaryButton onClick={props.onClose}>Cancel</SecondaryButton>
          <PrimaryButton type="submit" disabled={props.loading}>
            Save
          </PrimaryButton>
        </ButtonBar>
      </div>
    );
  };

  const tempPhoneNumber = props?.creationStatus?.tempPhoneNumber || '';
  const isCreationStillProcessing = !!props.creationStatus?.creationStillProcessing;
  const isCreatingPhoneTenant =
    props.creationStatus?.phoneTenantCreationRequested ??
    !!props.values.createPhoneTenant;

  return (
    <>
      <div className="location-form">
        <Form className={formLayout}>
          <div>
            <div className={formGroup}>
              <div className={flex}>
                <label className={marginRight5} htmlFor="Name">
                  Location Name
                </label>
                <InfoTooltip
                  body={
                    <div>
                      This field will update: <div>WAM: Location Name</div>{' '}
                      <div>Phone Data: Tenant Name</div>
                    </div>
                  }
                  tooltipPlacement="right"
                />
              </div>
              <Field type="text" id="Name" name="Name" className="form-control" />
              {props.touched.Name && props.errors.Name && (
                <div className={validationError}>{props.errors.Name}</div>
              )}
            </div>
            {props.isNew && !isExternalTruelarkLocation && (
              <div className={formGroup}>
                <label htmlFor="Slug">Slug</label>
                <Field type="text" id="Slug" name="Slug" className="form-control" />
                {props.touched.Slug && props.errors.Slug && (
                  <div className={validationError}>{props.errors.Slug}</div>
                )}
              </div>
            )}
            {props.isNew && !isExternalTruelarkLocation && (
              <div className={formGroup}>
                <label htmlFor="SalesforceID">SF Account ID</label>
                <Field
                  type="text"
                  id="SalesforceID"
                  name="SalesforceID"
                  className="form-control"
                />
                {props.touched.SalesforceID && props.errors.SalesforceID && (
                  <div className={validationError}>{props.errors.SalesforceID}</div>
                )}
              </div>
            )}
            {props.isNew && isExternalTruelarkLocation && (
              <div className={formGroup}>
                <label htmlFor="BusinessId">Truelark Business ID</label>
                <Field
                  type="text"
                  id="BusinessId"
                  name="BusinessId"
                  className="form-control"
                />
                {props.touched.BusinessId && props.errors.BusinessId && (
                  <div className={validationError}>{props.errors.BusinessId}</div>
                )}
              </div>
            )}
            <div className={formGroup}>
              <label htmlFor="Email">Office Email</label>
              <Field type="email" id="Email" name="Email" className="form-control" />
              {props.touched.Email && props.errors.Email && (
                <div className={validationError}>{props.errors.Email}</div>
              )}
              {isWeaveUserWithExternalType && (
                <Text size="medium" color="error">
                  Note: For Weave email id, the location type will be overridden to "Dev".
                </Text>
              )}
            </div>
            {!props.isNew && (
              <div className={formGroup}>
                <div className={flex}>
                  <label className={marginRight5} htmlFor="Phone">
                    Outbound Caller Number
                  </label>
                  <InfoTooltip
                    body={
                      <div>
                        This field will update: <div>WAM: Phone Number</div>{' '}
                        <div>Phone Data: Caller Number</div>
                      </div>
                    }
                    tooltipPlacement="right"
                  />
                </div>
                <Field type="text" id="Phone" name="Phone" className="form-control" />
              </div>
            )}
            <div className={formGroup}>
              <div className={flex}>
                <label className={marginRight5} htmlFor="Timezone">
                  Timezone
                </label>
                <InfoTooltip
                  body={
                    <div>
                      This field will update: <div>WAM: Timezone</div>
                      <div>Phone Data: Timezone</div>
                    </div>
                  }
                  tooltipPlacement="right"
                />
              </div>
              <Field
                component="select"
                id="Timezone"
                name="Timezone"
                className="form-control"
              >
                {Timezones.map((timezone) => {
                  return (
                    <option key={timezone.ID} value={timezone.Value}>
                      {timezone.Value}
                    </option>
                  );
                })}
              </Field>
            </div>
            <div className={formGroup}>
              <label htmlFor="Type">Type</label>
              <Field
                component="select"
                id="Type"
                name="Type"
                className="form-control"
                disabled={isExternalTruelarkLocation}
              >
                {isExternalTruelarkLocation ? (
                  <option value={LocationTypes.External}>External</option>
                ) : (
                  <>
                    <option value={LocationTypes.Regular}>Regular</option>
                    <option value={LocationTypes.Demo}>Demo</option>
                    <option value={LocationTypes.Dev}>Dev</option>
                  </>
                )}
              </Field>
            </div>
            <div className={formGroup}>
              <label htmlFor="VerticalID">Vertical</label>
              <Field
                component="select"
                id="VerticalID"
                name="VerticalID"
                className="form-control"
              >
                {getVerticalList().map((vertical) => (
                  <option value={vertical.key} key={vertical.key}>
                    {vertical.value}
                  </option>
                ))}
              </Field>
            </div>
            <div className={cx(flex, 'form-group')}>
              <div>
                <label htmlFor="Active">Active</label>
                <Field
                  id="Active"
                  name="Active"
                  className="form-control"
                  render={(field) => (
                    <ToggleSwitch
                      {...field}
                      label=""
                      on={props.values.Active}
                      onChange={() => {
                        props.setFieldValue('Active', !props.values.Active);
                      }}
                    />
                  )}
                />
              </div>
              {!props.values.Active && (
                <p className={warningText}>
                  <strong>WARNING:</strong> Marking this office inactive will prevent
                  users from using this location. Please be sure this effect is desirable
                  before saving.
                </p>
              )}
            </div>

            {props.allowSetParent && (
              <div>
                <label htmlFor="ParentID">
                  Parent Location{' '}
                  <Text as="span" size="medium" color="error">
                    (Optional - DO NOT USE UNLESS ABSOLUTELY SURE)
                  </Text>
                </label>
                <Field name="ParentID">
                  {({ field, form }) =>
                    !field.value ? (
                      <LocationSearchContainer
                        restrictToLocations
                        onSelect={(selection) =>
                          form.setFieldValue('ParentID', selection.LocationID)
                        }
                      />
                    ) : (
                      <div>
                        <span>{field.value}</span>{' '}
                        <PrimaryButton
                          size="small"
                          destructive
                          color="error"
                          onClick={() => form.setFieldValue('ParentID', null)}
                        >
                          Clear Parent
                        </PrimaryButton>
                      </div>
                    )
                  }
                </Field>
              </div>
            )}
          </div>

          {props.isNew && (
            <div>
              <div className={`form-group ${flex}`}>
                <Field
                  id="createPhoneTenant"
                  name="createPhoneTenant"
                  className={`form-control ${marginRight5}`}
                  render={(field) => (
                    <ToggleSwitch
                      {...field}
                      label=""
                      on={props.values.createPhoneTenant}
                      onChange={() => {
                        props.setFieldValue(
                          'createPhoneTenant',
                          !props.values.createPhoneTenant
                        );
                      }}
                      disabled={isExternalTruelarkLocation}
                    />
                  )}
                />
                <label htmlFor="createPhoneTenant" className={inlineFormSpacer}>
                  Create Phone Tenant
                </label>
              </div>
              {props.values.createPhoneTenant && (
                <div>
                  <div className={formGroup}>
                    <div className={flex}>
                      <label htmlFor="caller_number" className={marginRight5}>
                        {isExternalTruelarkLocation
                          ? 'External Phone Number'
                          : 'Outbound Caller Number'}
                      </label>
                      {isExternalTruelarkLocation && (
                        <InfoTooltip
                          body={
                            '(Optional, area code will be used for Weave phone number)'
                          }
                          tooltipPlacement="right"
                        />
                      )}
                    </div>

                    <Field
                      type="text"
                      id="caller_number"
                      name="caller_number"
                      className="form-control"
                    />
                    {props.touched.caller_number && props.errors.caller_number && (
                      <div className={validationError}>{props.errors.caller_number}</div>
                    )}
                  </div>
                  {!isExternalTruelarkLocation && (
                    <>
                      <div className={formGroup}>
                        <label htmlFor="street1">Street 1</label>
                        <Field
                          type="text"
                          id="street1"
                          name="street1"
                          className="form-control"
                        />
                        {props.touched.street1 && props.errors.street1 && (
                          <div className={validationError}>{props.errors.street1}</div>
                        )}
                      </div>
                      <div className={formGroup}>
                        <label htmlFor="street2">Street 2</label>
                        <Field
                          type="text"
                          id="street2"
                          name="street2"
                          className="form-control"
                        />
                      </div>
                      <div className={formGroup}>
                        <label htmlFor="city">City</label>
                        <Field
                          type="text"
                          id="city"
                          name="city"
                          className="form-control"
                        />
                        {props.touched.city && props.errors.city && (
                          <div className={validationError}>{props.errors.city}</div>
                        )}
                      </div>
                      <div className={formGroup}>
                        <label htmlFor="state">State</label>
                        <StatesTypeahead onChange={statesTypeaheadOnChange} />
                        {props.touched.state && props.errors.state && (
                          <div className={validationError}>{props.errors.state}</div>
                        )}
                      </div>
                      <div className={formGroup}>
                        <label htmlFor="zip">Zip code</label>
                        <Field type="text" id="zip" name="zip" className="form-control" />
                        {props.touched.zip && props.errors.zip && (
                          <div className={validationError}>{props.errors.zip}</div>
                        )}
                      </div>
                      <div className={formGroup}>
                        <label htmlFor="country">Country</label>
                        <Field
                          type="text"
                          id="country"
                          name="country"
                          className="form-control"
                          disabled
                        />
                      </div>
                    </>
                  )}
                </div>
              )}
              {isExternalTruelarkLocation && (
                <div className={formGroup}>
                  <div className={flex}>
                    <label className={marginRight5} htmlFor="template">
                      Tenant Type
                    </label>
                    <InfoTooltip
                      body={
                        <div>
                          <div>Voice AI: Truelark Standalone + Voice AI</div>
                          <div>Voicemail Only: Truelark Standalone for 10DLC</div>
                        </div>
                      }
                      tooltipPlacement="right"
                    />
                  </div>
                  <Field
                    component="select"
                    id="template"
                    name="template"
                    className="form-control"
                  >
                    <option
                      value={Template.TEMPLATE_VOICE_AI}
                      key={Template.TEMPLATE_VOICE_AI}
                    >
                      Voice AI
                    </option>
                    <option
                      value={Template.TEMPLATE_VOICEMAIL_ONLY}
                      key={Template.TEMPLATE_VOICEMAIL_ONLY}
                    >
                      Voicemail Only
                    </option>
                  </Field>
                </div>
              )}
            </div>
          )}
          <div className={formAction}>{renderButtons()}</div>
        </Form>
      </div>
      <Modal
        onClose={() => {
          closeCreationStatusModal();
        }}
        show={props.loading || !!props.creationStatus}
      >
        <Modal.Header>Creation Status</Modal.Header>
        <Modal.Body>
          {props.loading && (
            <div className={loader}>
              <Loader />
              <Text size="medium">
                Creating the location. This can take a moment while provisioning
                completes.
              </Text>
            </div>
          )}
          {isCreationStillProcessing && (
            <div className={creationStatusContainer}>
              <div className={creationStatusListItem}>
                <LocationAlert color="warning">
                  Location creation is still processing. Search for the location in a few
                  minutes before trying again.
                </LocationAlert>
              </div>
              <PrimaryButton
                className={creationStatusButton}
                onClick={() => {
                  closeCreationStatusModal();
                }}
              >
                Okay
              </PrimaryButton>
            </div>
          )}
          {props.creationStatus && !isCreationStillProcessing && (
            <div className={creationStatusContainer}>
              <div className={creationStatusListItem}>
                {props.creationStatus.locationCreatedSuccessfully ? (
                  <LocationAlert color="success">
                    Location created successfully.
                  </LocationAlert>
                ) : (
                  <LocationAlert color="danger">Location was not created.</LocationAlert>
                )}
              </div>
              {isCreatingPhoneTenant && (
                <>
                  <div className={creationStatusListItem}>
                    {props.creationStatus.phoneDataTenantCreatedSuccessfully ? (
                      <LocationAlert color="success">
                        Phone tenant created successfully.
                      </LocationAlert>
                    ) : (
                      <LocationAlert color="danger">
                        Phone tenant was not created.
                      </LocationAlert>
                    )}
                  </div>
                  <div className={creationStatusListItem}>
                    {props.creationStatus.customizationFlagsSetSuccessfully ? (
                      <LocationAlert color="success">
                        Customization flags setup successfully.
                      </LocationAlert>
                    ) : (
                      <LocationAlert color="danger">
                        Customization flags were not setup.
                      </LocationAlert>
                    )}
                  </div>
                  <div className={creationStatusListItem}>
                    {props.creationStatus.salesforceUpdatedSuccessfully ? (
                      <LocationAlert color="success">
                        Updated Salesforce successfully.
                      </LocationAlert>
                    ) : (
                      <LocationAlert color="danger">
                        Updated Salesforce unsuccessfully.
                      </LocationAlert>
                    )}
                  </div>
                </>
              )}
              {props?.creationStatus?.locationProvisionError?.length >= 1 && (
                <div className={creationStatusListItem}>
                  <LocationAlert color="danger">
                    Provisioning error: {props?.creationStatus?.locationProvisionError}
                  </LocationAlert>
                </div>
              )}
              {!!tempPhoneNumber && (
                <LocationAlert color="success">
                  <CopyToClipboardButton copyText={tempPhoneNumber}>
                    Temp. Phone Number: {tempPhoneNumber}
                  </CopyToClipboardButton>
                </LocationAlert>
              )}

              <PrimaryButton
                className={creationStatusButton}
                onClick={() => {
                  closeCreationStatusModal();
                }}
              >
                Okay
              </PrimaryButton>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};
